/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import "react-reflex/styles.css";

import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import CreateRecordButton from "../input/CreateRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
import MachineTypeEmbed from "./MachineTypeEmbed";
import layoutConf from "../../config/layoutConf";

/**
 * @COMPONENT_NAME MachineTypeTimeSeries
 * @DESCRIPTION 설비 유형과 설비 유형별 시계열 데이터 목록 관리 화면을 보여준다.
 *              react-flex를 통해 화면을 나누고 좌측에는 설비 유형 화면을 임베디드한 컴포넌트를 통해
 *              설비 유형을 보여주고 우측에는 설비 유형별 시계열 데이터 목록을 보여준다.
 *
 * @STATE
 *    - {Object} schema - 테이블 스키마 정보
 *    - {Object} grid - 그리드 객체 참조
 *    - {Object} api - API 객체 참조
 *    - {Object} machineTypeTimeSeriesSelect - 선택된 컨텍스트 데이터
 *    - {Object} selectedMachineType - 선택된 설비 유형 데이터
 *    - {number} height - 컴포넌트 높이 값
 *
 * @HANDLERS
 *    - {Function} handleResize - 화면 크기 변경 시 컴포넌트 높이를 조정하는 함수
 *    - {Function} onApiInitialized - API 초기화 완료 시 호출되는 함수
 *    - {Function} onInitialized - 그리드 초기화 완료 시 호출되는 함수
 *
 * @CALLBACKS
 *    - {Function} handleMachineTypeSelect - 설비 유형 선택 콜백
 *    - {Function} onSuccess - 작업 성공 시 알림 콜백
 *    - {Function} onFailure - 작업 실패 시 오류 알람 콜백
 *    - {Function} onNotifyWarning - 경고 알림 콜백
 *    - {Function} notifySuccess - 성공 알림 콜백
 *    - {Function} notifyFailure - 실패 알림 콜백
 *    - {Function} notifyWarning - 경고 알림 콜백
 */

const MachineTypeTimeSeries = () => {
	const table = "MachineTypeTimeSeries";
	const view = "VwMachineTypeTimeSeries";

	const [height, setHeight] = useState(0);

	const [schema, setSchema] = useState();
	const [grid, setGrid] = useState();
	const [api, setApi] = useState();
	const [machineTypeTimeSeriesSelect, setMachineTypeTimeSeriesSelect] =
		useState();
	const [selectedMachineType, setSelectedMachineType] = useState();

	const { t } = useTranslation();
	const setNotification = useSetRecoilState(notificationState);

	const handleResize = () => {
		setHeight(document.documentElement.offsetHeight - 96);
	};

	useEffect(() => {
		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		let mounted = true;

		async function schemaSetting() {
			let schema = await getSchema(table);
			if (mounted) {
				setSchema(schema);
			}
		}

		schemaSetting();
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		(async () => {
			if (grid) {
				grid.selectionChanged.addHandler((grid, event) => {
					if (grid.selectedItems && grid.selectedItems[0]) {
						setMachineTypeTimeSeriesSelect(grid.selectedItems[0]);
					} else {
						setMachineTypeTimeSeriesSelect([]);
					}
				});

				grid.itemsSourceChanged.addHandler((grid, event) => {
					if (grid.selectedItems && grid.selectedItems[0]) {
						setMachineTypeTimeSeriesSelect(grid.selectedItems[0]);
					} else {
						setMachineTypeTimeSeriesSelect([]);
					}
				});
			}
		})();
	}, [grid]);

	useEffect(() => {
		setApi((api) => {
			if (api) {
				api.refresh();
			}

			return api;
		});
	}, [selectedMachineType]);

	// 설비 유형 선택 처리
	const handleMachineTypeSelect = (machineType) => {
		setSelectedMachineType(
			machineType && machineType.length !== 0 ? machineType : null
		);
	};

	const onApiInitialized = (api) => {
		setApi(api);
	};

	const onInitialized = (grid) => {
		setGrid(grid);
	};

	const notifySuccess = (msg) => {
		setNotification({
			open: true,
			severity: "success",
			msg: msg,
		});
	};

	const notifyFailure = (msg) => {
		setNotification({
			open: true,
			severity: "error",
			msg: msg,
		});
	};

	const notifyWarning = (msg) => {
		setNotification({
			open: true,
			severity: "warning",
			msg: msg,
		});
	};

	const onSuccess = (msg) => {
		notifySuccess(msg);
		if (api) {
			api.refresh();
		}
	};

	const onFailure = (reason) => {
		notifyFailure(reason);
	};

	const onNotifyWarning = (msg) => {
		notifyWarning(msg);
		if (api) {
			api.refresh();
		}
	};

	return (
		<div style={{ height: height }}>
			<ReflexContainer orientation="vertical">
				<ReflexElement flex={0.5}>
					<MachineTypeEmbed
						onSelect={handleMachineTypeSelect}
					/>
				</ReflexElement>

				<ReflexSplitter
					style={{ width: layoutConf.reflex.splitterWidth }}
				/>

				<ReflexElement flex={0.5}>
					<BasicCrudScreenType
						id={table}
						headerTitle={t("term.machineTypeTimeSeries")}
						table={table}
						view={view}
						onModifyViewUrl={(url) =>
							`${url}/${selectedMachineType?.machineTypeCode}`
						}
						onInitialized={onInitialized}
						onApiInitialized={onApiInitialized}
						noCreate
						noEdit
						noDelete
						noExcel
						onAppendButtonInFrontOfSearch={() => (
							<>
								<BasicCrudScreenActionItemLayout>
									<AccessControl
										accessTarget={schema && schema.name}
										accessMethod="POST"
									>
										<CreateRecordButton
											id={table}
											schema={schema}
											refresh={api}
											disabled={!selectedMachineType}
											setInitialDataCallback={(
												formData
											) => {
												formData["machineTypeCode"] =
													selectedMachineType?.machineTypeCode;
											}}
											onSuccess={onSuccess}
										/>
									</AccessControl>
								</BasicCrudScreenActionItemLayout>

								<BasicCrudScreenActionItemLayout>
									<AccessControl
										accessTarget={schema && schema.name}
										accessMethod={
											schema &&
											(schema.updateWithPatch
												? "PATCH"
												: "PUT")
										}
									>
										<UpdateRecordButton
											id={table}
											schema={schema}
											grid={grid}
											refresh={api}
											disabled={
												machineTypeTimeSeriesSelect?.length ===
												0
											}
											onSuccess={notifySuccess}
										/>
									</AccessControl>
								</BasicCrudScreenActionItemLayout>

								<BasicCrudScreenActionItemLayout>
									<AccessControl
										accessTarget={schema && schema.name}
										accessMethod="DELETE"
									>
										<DeleteRecordButton
											id={table}
											schema={schema}
											grid={grid}
											refresh={api}
											disabled={
												machineTypeTimeSeriesSelect?.length ===
												0
											}
											color="secondary"
											onSuccess={onSuccess}
											onFailure={onFailure}
											onPartialFailure={onNotifyWarning}
										/>
									</AccessControl>
								</BasicCrudScreenActionItemLayout>
							</>
						)}
					/>
				</ReflexElement>
			</ReflexContainer>
		</div>
	);
};

export default MachineTypeTimeSeries;
